/**
 * Central Axios instance for the EHR Practice Portal.
 *
 * - baseURL: "/api/v1" (same-origin via Vite proxy / nginx reverse proxy)
 * - Access + CSRF tokens live in memory (not localStorage)
 * - Refresh token lives in HttpOnly cookie (ehr_refresh)
 */
import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from "axios";
import type { ApiResponse, TokenPair, PaginatedResponse } from "@/types";
import { shouldSuppressAuthRedirect } from "@/lib/session-idle";
import { isAuthFlowUrl, toUserMessage } from "@/lib/user-messages";
import { toast } from "@/lib/toast";
import {
  dispatchPlacDenied,
  getBreakGlassHeaders,
  isPlacDeniedMessage,
} from "@/lib/break-glass";

const SESSION_KEY = "ehr_session_id";
const ACCESS_STORAGE_KEY = "ehr_access_token";
const CSRF_STORAGE_KEY = "ehr_csrf_token";
const REFRESH_STORAGE_KEY = "ehr_refresh_token";
const MUTATING_METHODS = new Set(["post", "put", "patch", "delete"]);

const resolvedApiOrigin = (
  import.meta.env.VITE_API_URL as string | undefined
)?.replace(/\/$/, "");
export const API_BASE_PATH = resolvedApiOrigin
  ? `${resolvedApiOrigin}/api/v1`
  : "/api/v1";

/**
 * Build a WebSocket URL for an /api/v1 path (e.g. "/ws", "/ai/scribe/stream").
 * Azure Static Web Apps rewrites do not upgrade WebSockets (503), so when
 * VITE_API_URL is set we connect directly to the backend origin.
 */
export function buildWebSocketUrl(apiV1Path: string): string {
  const path = apiV1Path.startsWith("/") ? apiV1Path : `/${apiV1Path}`;
  if (resolvedApiOrigin) {
    const u = new URL(resolvedApiOrigin);
    const proto = u.protocol === "https:" ? "wss:" : "ws:";
    return `${proto}//${u.host}/api/v1${path}`;
  }
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}/api/v1${path}`;
}

/** Warn when cross-origin API URL breaks HttpOnly refresh cookies. */
if (resolvedApiOrigin) {
  const msg =
    "[auth] VITE_API_URL is set - the ehr_refresh cookie may not be sent. " +
    "Remove VITE_API_URL and use the Vite /api proxy for local dev.";
  if (import.meta.env.DEV) {
    console.error(msg);
  } else {
    console.warn(msg);
  }
}

let accessToken: string | null = sessionStorage.getItem(ACCESS_STORAGE_KEY);
let csrfToken: string | null = sessionStorage.getItem(CSRF_STORAGE_KEY);

const apiClient = axios.create({
  baseURL: API_BASE_PATH,
  headers: { "Content-Type": "application/json" },
  timeout: 30_000,
  withCredentials: true,
});

/** Refresh this many ms before the access JWT expires (backend default TTL: 15 min). */
const REFRESH_BUFFER_MS = 2 * 60 * 1000;

function applyAuthHeaders(config: InternalAxiosRequestConfig): void {
  config.headers = (config.headers ?? {}) as InternalAxiosRequestConfig["headers"];
  const headers = config.headers as Record<string, unknown>;
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  const method = (config.method ?? "get").toLowerCase();
  if (csrfToken && MUTATING_METHODS.has(method)) {
    headers["X-CSRF-Token"] = csrfToken;
  }
  const breakGlassHeaders = getBreakGlassHeaders();
  for (const [key, value] of Object.entries(breakGlassHeaders)) {
    headers[key] = value;
  }
}

function accessTokenExpiresAtMs(token: string): number | null {
  try {
    const segment = token.split(".")[1];
    if (!segment) return null;
    const payload = JSON.parse(atob(segment.replace(/-/g, "+").replace(/_/g, "/"))) as {
      exp?: number;
    };
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

function isAccessTokenStale(token: string): boolean {
  const expMs = accessTokenExpiresAtMs(token);
  if (!expMs) return false;
  return Date.now() >= expMs - REFRESH_BUFFER_MS;
}

let proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null;

function clearProactiveRefreshTimer(): void {
  if (proactiveRefreshTimer) {
    clearTimeout(proactiveRefreshTimer);
    proactiveRefreshTimer = null;
  }
}

function scheduleProactiveRefresh(): void {
  clearProactiveRefreshTimer();
  const token = accessToken;
  if (!token) return;

  const expMs = accessTokenExpiresAtMs(token);
  if (!expMs) return;

  const delay = Math.max(0, expMs - REFRESH_BUFFER_MS - Date.now());
  proactiveRefreshTimer = setTimeout(() => {
    void refreshSession()
      .then(() => scheduleProactiveRefresh())
      .catch(() => {
        /* next API call or 401 handler will surface auth failure */
      });
  }, delay);
}

// ── Request interceptor: refresh stale JWT, attach headers ───────────

apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    if (
      accessToken &&
      isAccessTokenStale(accessToken) &&
      !config.url?.includes("/auth/refresh") &&
      !config.url?.includes("/auth/login")
    ) {
      try {
        await refreshSession();
      } catch (error) {
        if (refreshRejected) {
          throw error;
        }
        /* Transient refresh failure: allow the request; its 401 handler retries once. */
      }
    }
    applyAuthHeaders(config);
    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

// ── Response interceptor: refresh on 401 & unwrap ────────────────────

/** Single in-flight refresh - prevents rotation races from concurrent callers. */
let refreshPromise: Promise<string> | null = null;
let refreshRejected = false;

async function refreshSession(): Promise<string> {
  if (refreshRejected) {
    throw new Error("Session expired. Please sign in again.");
  }
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      // Send stored refresh token in body when available (cross-origin fallback
      // for when the HttpOnly cookie cannot cross eTLD+1 boundaries).
      const storedRefreshToken = sessionStorage.getItem(REFRESH_STORAGE_KEY);
      const body = storedRefreshToken ? { refresh_token: storedRefreshToken } : {};
      const { data } = await axios.post<ApiResponse<TokenPair>>(
        `${API_BASE_PATH}/auth/refresh`,
        body,
        { withCredentials: true },
      );
      const pair = data.data;
      const csrf = pair.csrf_token ?? csrfFromAccessToken(pair.access_token);
      if (!csrf) {
        throw new Error("Refresh response missing CSRF token");
      }
      setTokens(pair.access_token, csrf, pair.session_id, pair.refresh_token);
      return pair.access_token;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        refreshRejected = true;
        clearTokens();
      }
      throw error;
    }
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

type ApiErrorBody = {
  error?: { message?: string; details?: string; code?: number } | string;
  message?: string;
  service?: string;
  allergy_warnings?: string[];
  drug_interaction_warnings?: string[];
  issues?: Array<{ field: string; message: string; severity: string }>;
  error_count?: number;
};

const GENERIC_API_MESSAGES = new Set([
  "bad request",
  "unauthorized",
  "forbidden",
  "not found",
  "conflict",
  "internal server error",
]);

function extractRawServerMessage(body: ApiErrorBody | undefined): string | undefined {
  if (!body) return undefined;
  if (body.message?.trim()) return body.message.trim();
  if (!body.error) return undefined;
  if (typeof body.error === "string") return body.error;
  const message = body.error.message?.trim();
  const details = body.error.details?.trim();
  if (details && (!message || GENERIC_API_MESSAGES.has(message.toLowerCase()))) {
    return details;
  }
  return message || details;
}

function parseAxiosError(error: AxiosError): Error & {
  status?: number;
  url?: string;
  allergyWarnings?: string[];
  drugInteractionWarnings?: string[];
} {
  const url = error.config?.url;
  const authContext = isAuthFlowUrl(url) ? "login" : "general";

  if (!error.response) {
    const err = new Error(
      toUserMessage(error.message, undefined, url, authContext),
    ) as Error & { status?: number; url?: string };
    err.url = url;
    return err;
  }

  const body = error.response.data as ApiErrorBody;
  const status = error.response.status;
  const raw = extractRawServerMessage(body);
  const message = toUserMessage(raw, status, url, authContext);

  const err = new Error(message) as Error & {
    status?: number;
    url?: string;
    allergyWarnings?: string[];
    drugInteractionWarnings?: string[];
  };
  err.status = status;
  err.url = url;

  if (status === 422) {
    if (Array.isArray(body?.allergy_warnings)) {
      err.allergyWarnings = body.allergy_warnings;
    }
    if (Array.isArray(body?.drug_interaction_warnings)) {
      err.drugInteractionWarnings = body.drug_interaction_warnings;
    }
  }

  return err;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (shouldSuppressAuthRedirect()) {
        return Promise.reject(error);
      }

      if (refreshRejected) {
        clearTokens();
        if (!window.location.pathname.startsWith("/login")) {
          window.location.href = "/login";
        }
        return Promise.reject(parseAxiosError(error));
      }

      if (
        originalRequest.url?.includes("/auth/refresh") ||
        originalRequest.url?.includes("/auth/login") ||
        originalRequest.url?.includes("/auth/mfa") ||
        originalRequest.url?.includes("/auth/sso")
      ) {
        if (!originalRequest.url?.includes("/auth/login")) {
          clearTokens();
        }
        if (
          !originalRequest.url?.includes("/auth/login") &&
          !originalRequest.url?.includes("/auth/mfa") &&
          !originalRequest.url?.includes("/auth/sso") &&
          !window.location.pathname.startsWith("/login")
        ) {
          window.location.href = "/login";
        }
        return Promise.reject(parseAxiosError(error));
      }

      originalRequest._retry = true;

      try {
        await refreshSession();
        applyAuthHeaders(originalRequest);
        return apiClient(originalRequest);
      } catch (refreshError) {
        if (!shouldSuppressAuthRedirect()) {
          clearTokens();
          if (!window.location.pathname.startsWith("/login")) {
            window.location.href = "/login";
          }
        }
        return Promise.reject(parseAxiosError(refreshError as AxiosError));
      }
    }

    if (error.response) {
      const status = error.response.status;
      const body = error.response.data as ApiErrorBody;
      const raw = extractRawServerMessage(body);

      if (status === 403 && isPlacDeniedMessage(raw)) {
        dispatchPlacDenied({
          message: raw ?? "Access denied for this patient record.",
          url: error.config?.url,
        });
      }

      // Safety-net toasts for 403/402 on user-triggered mutations so the failure
      // isn't silent. They are `fallback` toasts: if the screen shows its own
      // error toast for this failure, these are dropped (no double toasts).
      // Skip service_not_enabled - feature UIs show one specific message.
      const parsed = parseAxiosError(error);
      const errorCode =
        typeof body?.error === "string"
          ? body.error
          : typeof body?.error === "object" && body.error && "message" in body.error
            ? String((body.error as { message?: string }).message ?? "")
            : "";
      if (
        status === 403 &&
        MUTATING_METHODS.has((error.config?.method ?? "get").toLowerCase()) &&
        !isPlacDeniedMessage(raw) &&
        errorCode !== "service_not_enabled" &&
        body?.service !== "telehealth"
      ) {
        toast({
          variant: "destructive",
          title: "You don't have access to do this",
          description:
            parsed.message === "You don't have permission to do that."
              ? "Your role doesn't allow this action. Ask your organization administrator if you need access."
              : parsed.message,
          fallback: true,
        });
      }
      // Billing lapse: the account is read-only. Records stay viewable; explain
      // why the save failed and where to fix it.
      // Plan feature not included (RequireFeature / monthly meter): explain
      // which plan includes it instead of failing silently.
      if (
        status === 402 &&
        (errorCode === "feature not available" || errorCode === "monthly quota reached") &&
        MUTATING_METHODS.has((error.config?.method ?? "get").toLowerCase())
      ) {
        const message = (body as { message?: string })?.message;
        toast({
          variant: "destructive",
          title: "Not included in your plan",
          description: message ?? "An administrator can upgrade under Settings → Organization → Billing.",
          fallback: true,
        });
      }
      if (status === 402 && errorCode === "account_read_only") {
        const message = (body as { message?: string })?.message;
        toast({
          variant: "destructive",
          title: "Account is read-only",
          description: `${message ?? "Your account is read-only due to its billing status."} Go to Settings → Organization → Billing.`,
          fallback: true,
        });
      }
      return Promise.reject(parsed);
    }

    if (error.request) {
      return Promise.reject(
        new Error(
          error.code === "ECONNABORTED"
            ? "This is taking longer than expected. Please try again."
            : "We couldn't reach the server. Check your internet connection and try again.",
        ),
      );
    }

    return Promise.reject(parseAxiosError(error));
  },
);

// ── Token helpers ────────────────────────────────────────────────────

export function setTokens(
  nextAccessToken: string,
  nextCsrfToken: string,
  sessionId?: string,
  nextRefreshToken?: string,
): void {
  refreshRejected = false;
  accessToken = nextAccessToken;
  csrfToken = nextCsrfToken;
  sessionStorage.setItem(ACCESS_STORAGE_KEY, nextAccessToken);
  sessionStorage.setItem(CSRF_STORAGE_KEY, nextCsrfToken);
  if (sessionId) {
    sessionStorage.setItem(SESSION_KEY, sessionId);
  }
  if (nextRefreshToken) {
    sessionStorage.setItem(REFRESH_STORAGE_KEY, nextRefreshToken);
  }
  scheduleProactiveRefresh();
}

export function clearTokens(): void {
  accessToken = null;
  csrfToken = null;
  sessionStorage.removeItem(ACCESS_STORAGE_KEY);
  sessionStorage.removeItem(CSRF_STORAGE_KEY);
  sessionStorage.removeItem(SESSION_KEY);
  sessionStorage.removeItem(REFRESH_STORAGE_KEY);
  clearProactiveRefreshTimer();
}

if (accessToken) {
  scheduleProactiveRefresh();
}

export function getSessionId(): string | null {
  return sessionStorage.getItem(SESSION_KEY);
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

/** Read CSRF claim from access JWT when the JSON body omits csrf_token. */
export function csrfFromAccessToken(token: string): string | null {
  try {
    const segment = token.split(".")[1];
    if (!segment) return null;
    const payload = JSON.parse(atob(segment.replace(/-/g, "+").replace(/_/g, "/"))) as {
      csrf?: string;
    };
    return typeof payload.csrf === "string" ? payload.csrf : null;
  } catch {
    return null;
  }
}

/** Attempt silent session restore via HttpOnly refresh cookie. */
export async function trySilentRefresh(): Promise<boolean> {
  try {
    await refreshSession();
    return true;
  } catch {
    return false;
  }
}

/** Typed GET helper that unwraps ApiResponse. */
export async function apiGet<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> {
  const { data } = await apiClient.get<ApiResponse<T>>(url, config);
  return data.data;
}

/** Typed GET helper that returns the raw body (no ApiResponse unwrap). */
export async function apiGetRaw<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> {
  const { data } = await apiClient.get<T>(url, config);
  return data;
}

/**
 * Typed GET helper for paginated list endpoints.
 */
export async function apiGetList<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<PaginatedResponse<T>> {
  const { data } = await apiClient.get<PaginatedResponse<T>>(url, config);
  return data;
}

/** Typed POST helper that unwraps ApiResponse. */
export async function apiPost<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const { data } = await apiClient.post<ApiResponse<T>>(url, body, config);
  return data.data;
}

/** Typed POST helper that returns the raw body (no ApiResponse unwrap). */
export async function apiPostRaw<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const { data } = await apiClient.post<T>(url, body, config);
  return data;
}

/** Typed PUT helper that unwraps ApiResponse. */
export async function apiPut<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const { data } = await apiClient.put<ApiResponse<T>>(url, body, config);
  return data.data;
}

/** Typed PUT helper that returns the raw body (no ApiResponse unwrap). */
export async function apiPutRaw<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const { data } = await apiClient.put<T>(url, body, config);
  return data;
}

/** Typed PATCH helper that unwraps ApiResponse. */
export async function apiPatch<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const { data } = await apiClient.patch<ApiResponse<T>>(url, body, config);
  return data.data;
}

/** Typed DELETE helper that unwraps ApiResponse. */
export async function apiDelete<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> {
  const { data } = await apiClient.delete<ApiResponse<T>>(url, config);
  return data.data;
}

/** Typed DELETE helper that returns the raw body (no ApiResponse unwrap). */
export async function apiDeleteRaw<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> {
  const { data } = await apiClient.delete<T>(url, config);
  return data;
}

export default apiClient;
